// Information one layer deep: the surface stays clean, and anything marked `data-tip` explains
// itself on hover. `data-tip="term:Department"` pulls a glossary entry; any other value is shown as
// rich text ({R}/{MANA} glyph tokens allowed). One global layer, so tooltips never clip in panels.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { RichText } from './Card';

/** A phone or tablet (no hover): prompts say "tap", and inspecting is tap-to-show. */
export const TOUCH = typeof matchMedia !== 'undefined' && matchMedia('(hover: none) and (pointer: coarse)').matches;
export const TAP = TOUCH ? 'Tap' : 'Click';

export const GLOSSARY: Record<string, { title: string; text: string }> = {
  Assistant: { title: 'Assistant', text: 'Your clerks. Each turn you send one to a room and take its effect. They come home at the end of the round. You can have at most 4, temporary ones included (5 once the Intern arrives).' },
  Department: { title: 'Department', text: 'The six rooms that hand out permits: Conjuration, Transmutation, Evocation, Divination, Enchantment and Procuration.' },
  permit: { title: 'Permits', text: 'The paperwork spells are cast with. Four colours: {R} Red, {Y} Yellow, {B} Blue, {G} Green.' },
  mana: { title: 'Mana', text: '{MANA} Buys spells at the Arcanium and pays some fees and abilities. You gain 1 at the start of every round.' },
  VP: { title: 'Victory Points', text: '{VP} From cast spells, City Problems, abilities and your Secret Agenda. Most VP after 8 rounds wins.' },
  spell: { title: 'Spells', text: 'Bought at the Arcanium, cast from your hand by paying their permit cost. Cast spells stay in front of you: they score VP and keep their ability.' },
  'Action card': { title: 'Action cards', text: 'One-shot memos. Draw them at the Archive, play them at the Planning Office.' },
  'City Problem': { title: 'City Problems', text: 'Public objectives. Claim one at the Grand Auditor’s Office when you meet its requirement. The first claimant scores more.' },
  Arcanium: { title: 'The Arcanium', text: 'The spell shop. Visit it to buy 1 spell from the display with mana. The display stays until someone refreshes it: a visitor may first pay 1 {MANA} to clear it and reveal 5 new spells.' },
  Archive: { title: 'The Archive', text: 'Draw Action cards.' },
  'Planning Office': { title: 'The Planning Office', text: 'Play Action cards from your hand.' },
  'Grand Auditor': { title: 'The Grand Auditor’s Office', text: 'Claim 1 City Problem whose requirement you meet.' },
  'free action': { title: 'Free action', text: 'Can be used any time during your turn, before or after placing your Assistant.' },
  'End Game': { title: 'End Game', text: 'Scored once, after the last round.' },
  'Secret Agenda': { title: 'Secret Agenda', text: 'Your hidden goal. Revealed and scored at the end of the game.' },
  Audit: { title: 'Audit', text: 'An audited room is closed to the other players for the rest of the round.' },
  'temporary Assistant': { title: 'Temporary Assistant', text: 'An extra clerk for this turn only. It goes back to the pool afterwards.' },
  rank: { title: 'Rank', text: 'Your title in the Ministry. It rises with your VP — purely for glory.' },
  Decree: { title: 'Decrees', text: 'Rules that apply to every player while in force. Cards enact them, and the Ministry issues one of its own at the start of rounds 3 and 5. Up to 4 are in force at once; a new one replaces the oldest. Each one still in force at the end scores 2 {VP} for its author.' },
  Gazette: { title: 'Decrees in force', text: `Rules that apply to everyone right now (up to 4). ${TOUCH ? 'Tap' : 'Hover'} one to read it. Enact your own, or repeal a rival’s, to change the rules for everyone.` },
  'Stamp of Precedence': {
    title: 'The Stamp of Precedence',
    text: 'Its holder goes first each round. It stays put until a rival takes it at the Office of Precedence; they go first from the next round.',
  },
  Intern: { title: 'Intern', text: 'The Afternoon Shift: from round 6, every player has a third Assistant for the rest of the game.' },
  'Under Review': { title: 'Under Review', text: 'An appealed spell: its abilities stop until the end of the next round. It keeps its VP.' },
  Revoke: { title: 'Revoke', text: 'A revoked spell returns to its owner’s hand and they lose its printed VP. Only rivals with at least as many VP as you can be revoked.' },
  Repeal: { title: 'Repeal', text: 'Strike a Decree in force. Its author no longer scores it.' },
  Resolution: {
    title: 'Resolutions',
    text: 'Printed on City Problems. The first player to claim a Problem passes its Resolution: it changes one rule for every player, from the next round, for the rest of the game. Hover a plaque to read it.',
  },
  'Annex room': {
    title: 'Annex rooms',
    text: 'Extra rooms opened by Resolutions into the two Annex bays at the front corners of the Ministry. With both bays full, the room that opened first closes for good.',
  },
  'last round': { title: 'Last round', text: 'Round 8, or round 7 once Early Closing is in force.' },
};

let hintLayer: HTMLDivElement | null = null;

/**
 * Click feedback: a short note that rises from the point you clicked and fades. Used when an
 * action isn't possible ("Needs 2 more Red permits") — explains without a modal or a toast.
 */
export function hint(x: number, y: number, text: string, kind: 'info' | 'warn' = 'warn'): void {
  if (!hintLayer) return;
  const el = document.createElement('div');
  el.className = `click-hint hint-${kind}`;
  el.textContent = text;
  hintLayer.appendChild(el);
  const w = el.offsetWidth;
  el.style.left = `${Math.max(8, Math.min(innerWidth - w - 8, x - w / 2))}px`;
  el.style.top = `${Math.max(64, y - 44)}px`;
  el.addEventListener('animationend', () => el.remove());
}

/**
 * Last pointer position, so code without an event (store actions) can place a hint, and whether
 * it was a finger (touch screens get tap-worded prompts).
 */
export const pointer = { x: innerWidth / 2, y: innerHeight / 2, touch: false };

export function TooltipLayer() {
  const [tip, setTip] = useState<{ text: string; rect: DOMRect } | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: -9999, top: -9999, below: false });

  useEffect(() => {
    let timer = 0;
    let current: HTMLElement | null = null;
    const show = (el: HTMLElement) => setTip({ text: el.dataset.tip!, rect: el.getBoundingClientRect() });
    const over = (e: PointerEvent) => {
      pointer.x = e.clientX;
      pointer.y = e.clientY;
      if (e.pointerType === 'touch') return; // touch: see `down`
      const el = (e.target as HTMLElement | null)?.closest?.<HTMLElement>('[data-tip]') ?? null;
      if (el === current) return;
      current = el;
      clearTimeout(timer);
      setTip(null);
      if (el) timer = window.setTimeout(() => show(el), 380);
    };
    const hide = () => {
      clearTimeout(timer);
      current = null;
      setTip(null);
    };
    // Touch has no hover. A tap on an explained element shows its tip (tap elsewhere to dismiss); on
    // a button, where the tap already does something, pressing and holding shows it instead.
    let held = false;
    const down = (e: PointerEvent) => {
      pointer.x = e.clientX;
      pointer.y = e.clientY;
      pointer.touch = e.pointerType === 'touch';
      const el = pointer.touch ? ((e.target as HTMLElement | null)?.closest?.<HTMLElement>('[data-tip]') ?? null) : null;
      const again = el !== null && el === current;
      hide();
      held = false;
      if (!el || again) return;
      current = el;
      if (el.closest('button, a, input, select')) {
        timer = window.setTimeout(() => {
          held = true;
          show(el);
        }, 450);
      } else show(el);
    };
    const up = () => {
      if (held) return;
      clearTimeout(timer);
    };
    // The release after a press-and-hold only showed the tip; it must not also press the button.
    const click = (e: MouseEvent) => {
      if (!held) return;
      held = false;
      e.preventDefault();
      e.stopPropagation();
    };
    window.addEventListener('pointermove', over, { passive: true });
    window.addEventListener('pointerdown', down, { capture: true });
    window.addEventListener('pointerup', up, { capture: true });
    window.addEventListener('pointercancel', up, { capture: true });
    window.addEventListener('click', click, { capture: true });
    window.addEventListener('wheel', hide, { passive: true });
    return () => {
      clearTimeout(timer);
      window.removeEventListener('pointermove', over);
      window.removeEventListener('pointerdown', down, { capture: true });
      window.removeEventListener('pointerup', up, { capture: true });
      window.removeEventListener('pointercancel', up, { capture: true });
      window.removeEventListener('click', click, { capture: true });
      window.removeEventListener('wheel', hide);
    };
  }, []);

  useLayoutEffect(() => {
    if (!tip || !ref.current) return;
    const el = ref.current;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const r = tip.rect;
    const below = r.top - h - 10 < 60;
    setPos({
      left: Math.max(8, Math.min(innerWidth - w - 8, r.left + r.width / 2 - w / 2)),
      top: below ? r.bottom + 10 : r.top - h - 10,
      below,
    });
  }, [tip]);

  const hintRef = (el: HTMLDivElement | null) => {
    hintLayer = el;
  };

  let body: React.ReactNode = null;
  if (tip) {
    const term = tip.text.startsWith('term:') ? GLOSSARY[tip.text.slice(5)] : null;
    const [title, text] = term ? [term.title, term.text] : tip.text.includes('|') ? (tip.text.split('|', 2) as [string, string]) : [null, tip.text];
    body = (
      <>
        {title && <div className="tip-title">{title}</div>}
        <div className="tip-body">
          <RichText text={text} />
        </div>
      </>
    );
  }
  return (
    <>
      <div className="hint-layer" ref={hintRef} aria-live="polite" />
      {tip && (
        <div ref={ref} className={`tooltip ${pos.below ? 'below' : ''}`} style={{ left: pos.left, top: pos.top }} role="tooltip">
          {body}
        </div>
      )}
    </>
  );
}
